import React from 'react';
import { ScrollView, View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Concentrado por proveedor" (anual, por comunidad).
// Pivote: (proveedor × rubro/servicio) × meses. Cada celda de mes = Monto + Fecha,
// con 3 estados:
//   • pagado    → monto + fecha de pago
//   • pendiente → servicio recurrente esperado ese mes, aún sin pagar (rojo)
//   • na        → no aplica ese mes (servicio de una sola vez)
//
// OJO: la columna de mes es el mes del SERVICIO (no el de la fecha de pago):
// "Administración de enero" cae en ENE aunque se pagó el 27/02.
//
// Mapeo backend (aún sin endpoint, y con una decisión pendiente):
//  • Pagos        → Accounting\Expense agrupados por proveedor × rubro × mes-servicio.
//  • RUBRO/Servicio → categoría del egreso. PENDIENTE DE DEFINIR la taxonomía:
//      (a) Vinte ExpenseType (vinte_expense_types, jerárquico/global) — vía
//          paymentRequest.expenseType, o vinte_expense_type_id directo en Expense; o
//      (b) Accounting ExpenseCategory (accounting_expenses_categories, por comunidad).
//      Por ahora el rubro va como texto libre (servicio).
//  • estado pendiente vs na → requiere un concepto de "servicio recurrente esperado"
//      que NO existe en los modelos (derivar de historial o modelarlo). En demo es
//      explícito por celda.
// TODO(backend): reemplazar DEMO por useQuery(communityId, año).
// ───────────────────────────────────────────────────────────────────────────

// Celda de un mes: o un pago concreto, o un marcador de estado.
type Celda = { monto: number; fecha: string } | 'pendiente' | 'na';

type Fila = {
  proveedor: string;
  servicio: string;       // rubro / categoría
  meses: Celda[];         // alineado con MESES
};

const COMMUNITY = 'Comunidad Real Bilbao A.C';
const ANIO = '2026';
const MESES = ['Ene', 'Feb', 'Mar']; // demo: el concentrado crece mes a mes

const p = (monto: number, fecha: string): Celda => ({ monto, fecha });

// ─── Datos DEMO (concentrado 2026, Ene–Mar) ──────────────────────────────────
const FILAS: Fila[] = [
  { proveedor: 'Comunidad Integral RR',     servicio: 'Administración',                 meses: [p(59044, '27/02/26'), p(59044, '27/02/26'), 'pendiente'] },
  { proveedor: 'Comunidad Integral RR',     servicio: 'Mantenimiento conjunto/privadas', meses: [p(106452.74, '27/02/26'), p(106452.74, '13/03/26'), 'pendiente'] },
  { proveedor: 'Comunidad Integral RR',     servicio: 'Recolección de basura',          meses: [p(7500, '27/02/26'), p(7500, '27/02/26'), 'pendiente'] },
  { proveedor: 'Comunidad Integral RR',     servicio: 'Vigilancia',                     meses: [p(107764, '27/02/26'), p(107764, '13/03/26'), 'pendiente'] },
  { proveedor: 'Asesores Aguirre',          servicio: 'Contabilidad',                   meses: [p(5628, '08/01/26'), p(5628, '19/02/26'), 'pendiente'] },
  { proveedor: 'Marybon',                   servicio: 'Pipas para riego',               meses: [p(1740, '22/01/26'), 'na', 'na'] },
  { proveedor: 'Telmex',                    servicio: 'Internet',                       meses: [p(489, '16/01/26'), p(489, '13/02/26'), p(599, '13/03/26')] },
  { proveedor: 'Tío Ninja',                 servicio: 'TAGS RFID',                       meses: [p(5591.2, '05/03/26'), 'na', 'na'] },
  { proveedor: 'Tío Ninja',                 servicio: 'App QR',                          meses: [p(4823.33, '17/03/26'), 'na', 'na'] },
  { proveedor: 'Grupo CC PYJ',              servicio: 'Sistema de riego',               meses: [p(179593, '13/02/26'), 'na', 'pendiente'] },
  { proveedor: 'Edmundo Munguía',           servicio: 'Recibos',                        meses: [p(5393.25, '22/01/26'), 'na', 'na'] },
  { proveedor: 'Comunidad Real Bilbao A.C', servicio: 'Traspasos',                      meses: [p(1800, '16/01/26'), p(12990, '19/02/26'), p(600, '13/03/26')] },
  { proveedor: 'Julio César Doroteo García', servicio: 'Asesoría jurídica',             meses: ['na', 'na', 'pendiente'] },
];

const isPago = (c: Celda): c is { monto: number; fecha: string } => typeof c === 'object';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

// Anchos
const PROV_W = 190;
const SERV_W = 200;
const MONTO_W = 116;
const FECHA_W = 92;
const TOTAL_W = 120;
const MONTH_W = MONTO_W + FECHA_W;

// Par de celdas (Monto | Fecha) de un mes, con estilo según estado.
function MonthCells({ celda }: { celda: Celda }) {
  if (celda === 'pendiente') {
    return (
      <>
        <View style={tw.style(`px-2 py-2 justify-center items-end border-r border-b border-red-300 bg-red-500`, { width: MONTO_W })}>
          <Text style={tw`text-white text-xs`}>$ -</Text>
        </View>
        <View style={tw.style(`px-2 py-2 justify-center items-center border-r border-b border-red-300 bg-red-500`, { width: FECHA_W })}>
          <Text style={tw`text-white text-xs font-700`}>PENDIENTE</Text>
        </View>
      </>
    );
  }
  if (celda === 'na') {
    return (
      <>
        <View style={tw.style(`px-2 py-2 justify-center items-end border-r border-b border-gray-100 bg-gray-50`, { width: MONTO_W })}>
          <Text style={tw`text-gray-300 text-xs`}>$ -</Text>
        </View>
        <View style={tw.style(`px-2 py-2 justify-center items-center border-r border-b border-gray-100 bg-gray-50`, { width: FECHA_W })}>
          <Text style={tw`text-gray-400 text-xs`}>N/A</Text>
        </View>
      </>
    );
  }
  return (
    <>
      <View style={tw.style(`px-2 py-2 justify-center items-end border-r border-b border-gray-100`, { width: MONTO_W })}>
        <Text style={tw`text-gray-800 text-xs font-500`}>{money(celda.monto)}</Text>
      </View>
      <View style={tw.style(`px-2 py-2 justify-center items-center border-r border-b border-gray-100`, { width: FECHA_W })}>
        <Text style={tw`text-gray-500 text-xs`}>{celda.fecha}</Text>
      </View>
    </>
  );
}

export default function ConcentradoPorProveedor({ communityId, date }: { communityId?: string; date?: Date }) {
  // Total por fila (suma de lo pagado en el año).
  const rowTotal = (f: Fila) => f.meses.reduce((acc, c) => acc + (isPago(c) ? c.monto : 0), 0);

  // Total por mes (suma de lo pagado de todas las filas).
  const monthTotals = MESES.map((_, i) => FILAS.reduce((acc, f) => acc + (isPago(f.meses[i]) ? (f.meses[i] as any).monto : 0), 0));
  const grandTotal = monthTotals.reduce((a, b) => a + b, 0);

  return (
    <View style={tw`px-4 pb-10`}>
      <View style={tw`row justify-between items-center mb-4`}>
        <View>
          <Text style={tw`text-lg font-700 text-secondary`}>Concentrado por proveedor {ANIO}</Text>
          <Text style={tw`text-gray-400 text-xs`}>{COMMUNITY}</Text>
        </View>
        <Text style={tw`text-gray-400`}>{FILAS.length} rubros</Text>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator style={tw`bg-white rounded-xl border border-gray-100`}>
        <View>
          {/* Cabecera superior: meses agrupando Monto+Fecha */}
          <View style={tw`row`}>
            <View style={tw.style({ width: PROV_W + SERV_W })} />
            {MESES.map(m => (
              <View key={m} style={tw.style(`px-2 py-1.5 items-center justify-center bg-secondary border-r border-white`, { width: MONTH_W })}>
                <Text style={tw`text-white font-700 text-xs uppercase`}>{m}</Text>
              </View>
            ))}
            <View style={tw.style({ width: TOTAL_W })} />
          </View>

          {/* Cabecera de columnas */}
          <View style={tw`row border-b border-gray-300 bg-secondary/10`}>
            <View style={tw.style(`px-2 py-2 border-r border-gray-200`, { width: PROV_W })}><Text style={tw`text-secondary font-600 text-xs`}>Proveedor</Text></View>
            <View style={tw.style(`px-2 py-2 border-r border-gray-200`, { width: SERV_W })}><Text style={tw`text-secondary font-600 text-xs`}>Servicio / Rubro</Text></View>
            {MESES.map(m => (
              <React.Fragment key={m}>
                <View style={tw.style(`px-2 py-2 items-end border-r border-gray-200`, { width: MONTO_W })}><Text style={tw`text-secondary font-600 text-xs`}>Monto</Text></View>
                <View style={tw.style(`px-2 py-2 items-center border-r border-gray-200`, { width: FECHA_W })}><Text style={tw`text-secondary font-600 text-xs`}>Fecha</Text></View>
              </React.Fragment>
            ))}
            <View style={tw.style(`px-2 py-2 items-end`, { width: TOTAL_W })}><Text style={tw`text-secondary font-600 text-xs`}>Total año</Text></View>
          </View>

          {/* Filas */}
          {FILAS.map((f, idx) => (
            <View key={`${f.proveedor}-${f.servicio}`} style={tw.style(`row`, idx % 2 === 1 && `bg-gray-50/40`)}>
              <View style={tw.style(`px-2 py-2 justify-center border-r border-b border-gray-100`, { width: PROV_W })}>
                <Text style={tw`text-gray-700 text-xs font-500`} numberOfLines={2}>{f.proveedor}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 justify-center border-r border-b border-gray-100`, { width: SERV_W })}>
                <Text style={tw`text-gray-600 text-xs`} numberOfLines={2}>{f.servicio}</Text>
              </View>
              {f.meses.map((c, i) => <MonthCells key={i} celda={c} />)}
              <View style={tw.style(`px-2 py-2 justify-center items-end border-b border-gray-100 bg-gray-50/60`, { width: TOTAL_W })}>
                <Text style={tw`text-gray-900 text-xs font-700`}>{money(rowTotal(f))}</Text>
              </View>
            </View>
          ))}

          {/* Totales por mes */}
          <View style={tw`row border-t-2 border-secondary/40 bg-gray-50`}>
            <View style={tw.style(`px-2 py-2 justify-center`, { width: PROV_W + SERV_W })}>
              <Text style={tw`text-secondary font-700 text-xs`}>TOTALES</Text>
            </View>
            {monthTotals.map((t, i) => (
              <View key={i} style={tw.style(`px-2 py-2 justify-center items-end border-r border-gray-100`, { width: MONTH_W })}>
                <Text style={tw`text-gray-900 font-700 text-xs`}>{money(t)}</Text>
              </View>
            ))}
            <View style={tw.style(`px-2 py-2 justify-center items-end`, { width: TOTAL_W })}>
              <Text style={tw`text-secondary font-700 text-xs`}>{money(grandTotal)}</Text>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* <Text style={tw`text-gray-300 text-xs mt-3`}>
        Datos demo · estado por celda (pagado/pendiente/na) · rubro como texto (taxonomía por definir) · communityId: {communityId ?? '—'}
      </Text> */}
    </View>
  );
}
